import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { formatDateToDDMMYYYY } from '../../utils';
import AgencyInfoDialog from './AgencyInfoDialog';

const stickyCellStyle = {
  background: '#262626',
  fontWeight: 'bold',
  minWidth: 50,
  color: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

interface PendingInvoice {
  id: number;
  client_id: string;
  deal_id: number;
  pipeline_letter: string;
  vat_rate: string;
  selling_price: string;
  reg_plate: string;
  created_at: string;
  processed: number;
  agency_name: string;
  agency_id: number;
  /** Qualification serveur de l'attente (SKIP-466). */
  billing_state?: 'invoiced' | 'ready' | 'blocked_no_receipt' | 'awaiting_receipt';
  billing_reason?: string;
  invoice_number?: string | null;
}

const PENDING_STATE_STYLE: Record<string, { color: string; label: string }> = {
  invoiced: { color: '#9e9e9e', label: 'Déjà facturée' },
  ready: { color: '#4caf50', label: 'Prête' },
  blocked_no_receipt: { color: '#f44336', label: 'Sans récépissé' },
  awaiting_receipt: { color: '#ff9800', label: 'En attente' },
};

interface AgencyGroup {
  agencyId: number;
  agency_name: string;
  clients: ClientGroup[];
}

interface ClientGroup {
  client_id: string;
  invoices: PendingInvoice[];
}

const agencyColors = [
  'rgba(253, 209, 95, 0.3)',
  'rgba(253, 199, 95, 0.3)',
  'rgba(253, 187, 95, 0.3)',
  'rgba(239, 143, 53, 0.3)',
];

const CGPendingInvoicesList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [allRows, setAllRows] = useState<PendingInvoice[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await makeAuthenticatedApiCall('get', '/api/qonto/pending-invoices');
      setAllRows((response.data as PendingInvoice[]) || []);
    } catch (error) {
      console.error('Error fetching pending invoices:', error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const agencies = useMemo(() => {
    const uniqueAgencies = new Map<number, string>();
    allRows.forEach((row) => {
      if (row.agency_id && row.agency_name) {
        uniqueAgencies.set(row.agency_id, row.agency_name);
      }
    });
    return Array.from(uniqueAgencies.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allRows]);

  const isSearchActive = searchQuery.trim().length >= 2;

  const filteredRows = useMemo(() => {
    let filtered = allRows;

    if (isSearchActive) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter((row) => row.reg_plate.toLowerCase().includes(searchLower));
    } else {
      if (selectedTab === 0) {
        filtered = filtered.filter((row) => row.processed === 0);
      } else if (selectedTab === 1) {
        filtered = filtered.filter((row) => row.processed === 1);
      }

      if (selectedAgencyId !== 'all') {
        const agencyIdNum = parseInt(selectedAgencyId);
        filtered = filtered.filter((row) => row.agency_id === agencyIdNum);
      }
    }

    return filtered;
  }, [allRows, selectedTab, selectedAgencyId, searchQuery, isSearchActive]);

  const groupedByAgency = useMemo((): AgencyGroup[] => {
    const agencyMap = new Map<number, { agency_name: string; clientMap: Map<string, PendingInvoice[]> }>();

    filteredRows.forEach((row) => {
      if (!agencyMap.has(row.agency_id)) {
        agencyMap.set(row.agency_id, {
          agency_name: row.agency_name || 'N/A',
          clientMap: new Map(),
        });
      }
      const agency = agencyMap.get(row.agency_id)!;
      if (!agency.clientMap.has(row.client_id)) {
        agency.clientMap.set(row.client_id, []);
      }
      agency.clientMap.get(row.client_id)!.push(row);
    });

    return Array.from(agencyMap.entries())
      .map(([agencyId, data]) => ({
        agencyId,
        agency_name: data.agency_name,
        clients: Array.from(data.clientMap.entries()).map(([client_id, invoices]) => ({
          client_id,
          invoices,
        })),
      }))
      .sort((a, b) => a.agency_name.localeCompare(b.agency_name));
  }, [filteredRows]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleAgencyChange = (event: SelectChangeEvent) => {
    setSelectedAgencyId(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleOpen = (clientId: string) => {
    setSelectedClientId(clientId);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedClientId(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (isLoading) {
    return (
      <Grid container direction="column" justifyContent="center" alignItems="center" marginTop={2}>
        <Typography sx={{ marginBottom: 2 }} variant="body1">
          Chargement
        </Typography>
        <CircularProgress size={70} />
      </Grid>
    );
  }

  return (
    <Container maxWidth={false}>
      <Typography variant="h4" gutterBottom sx={{ marginTop: 4 }}>
        Liste des factures en attente
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Visualisez les factures en attente de traitement et celles déjà traitées.
      </Typography>

      {/* Filtres */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          className="inputElement"
          placeholder="Rechercher par immatriculation..."
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            minWidth: 300,
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.3s ease',
              '&.Mui-focused': {
                boxShadow: '0 0 0 2px rgba(239, 143, 53, 0.2)',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              fontSize: '0.9rem',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: -0.5 }}>
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="effacer la recherche"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                  sx={{
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(239, 143, 53, 0.1)',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel className="inputElement" id="agency-filter-label">
            Filtrer par agence
          </InputLabel>
          <Select
            labelId="agency-filter-label"
            id="agency-filter"
            value={selectedAgencyId}
            label="Filtrer par agence"
            className="inputElement"
            onChange={handleAgencyChange}
            disabled={isSearchActive}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            <MenuItem value="all">Toutes les agences</MenuItem>
            {agencies.map((agency) => (
              <MenuItem key={agency.id} value={agency.id.toString()}>
                {agency.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isSearchActive && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              backgroundColor: 'rgba(239, 143, 53, 0.1)',
              border: '1px solid rgba(239, 143, 53, 0.3)',
              animation: 'fadeIn 0.3s ease',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(-5px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {filteredRows.length} résultat{filteredRows.length > 1 ? 's' : ''} trouvé
              {filteredRows.length > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Onglets */}
      {!isSearchActive && (
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          className="DealsListTabs"
          aria-label="Pending invoices tabs"
          sx={{ mb: 3 }}
        >
          <Tab label="À traiter" />
          <Tab label="Déjà traités" />
        </Tabs>
      )}

      {/* Contenu groupé par agence */}
      {groupedByAgency.length === 0 ? (
        <Paper sx={{ p: 4 }} elevation={3}>
          <Typography variant="body1" align="center">
            Aucune facture trouvée pour cette sélection
          </Typography>
        </Paper>
      ) : (
        groupedByAgency.map((agencyGroup, agencyIndex) => (
          <Paper
            key={agencyGroup.agencyId}
            sx={{
              mb: 4,
              p: 2,
              backgroundColor: agencyColors[agencyIndex % agencyColors.length],
              borderRadius: 2,
            }}
            elevation={3}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#262626' }}>
              {agencyGroup.agency_name}
            </Typography>

            {agencyGroup.clients.map((client) => {
              const cgInvoices = client.invoices.filter((inv) => inv.pipeline_letter.toLowerCase() === 'f');
              const daInvoices = client.invoices.filter((inv) => inv.pipeline_letter.toLowerCase() === 'da');
              const cgCount = cgInvoices.length;
              const daCount = daInvoices.length;
              const totalHT = client.invoices.reduce((sum, inv) => sum + parseFloat(inv.selling_price), 0);

              const countDisplay =
                cgCount > 0 && daCount > 0
                  ? `${cgCount} CG et ${daCount} DA`
                  : cgCount > 0
                    ? `${cgCount} CG`
                    : daCount > 0
                      ? `${daCount} DA`
                      : `${client.invoices.length} facture${client.invoices.length > 1 ? 's' : ''}`;

              return (
                <Box key={client.client_id} sx={{ mb: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: '#464646', cursor: 'pointer', '&:hover': { color: '#ef8f35' } }}
                        onClick={() => handleOpen(client.client_id)}
                      >
                        Client {client.client_id}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {countDisplay} - Total HT : {totalHT.toFixed(2)} €
                      </Typography>
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: selectedTab === 1 || client.invoices[0]?.processed ? '#4caf50' : '#ff9800',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                        }}
                      >
                        {selectedTab === 1 || client.invoices[0]?.processed ? 'Traité' : 'En attente'}
                      </Box>
                    </Box>
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={stickyCellStyle}>Immatriculation</TableCell>
                          <TableCell sx={stickyCellStyle}>Deal ID</TableCell>
                          <TableCell sx={stickyCellStyle}>Prix</TableCell>
                          <TableCell sx={stickyCellStyle}>Pipeline</TableCell>
                          <TableCell sx={stickyCellStyle}>TVA</TableCell>
                          <TableCell sx={stickyCellStyle}>Date de création</TableCell>
                          <TableCell sx={stickyCellStyle}>Statut</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {client.invoices.map((row) => (
                          <TableRow
                            key={row.id}
                            sx={{
                              backgroundColor: row.processed
                                ? 'rgba(76, 175, 80, 0.05)'
                                : 'rgba(255, 152, 0, 0.05)',
                            }}
                          >
                            <TableCell>{row.reg_plate}</TableCell>
                            <TableCell>{row.deal_id}</TableCell>
                            <TableCell>{parseFloat(row.selling_price).toFixed(2)} €</TableCell>
                            <TableCell>{row.pipeline_letter.toUpperCase()}</TableCell>
                            <TableCell>{row.vat_rate}</TableCell>
                            <TableCell>{formatDateToDDMMYYYY(row.created_at)}</TableCell>
                            <TableCell>
                              {row.processed ? (
                                <Tooltip title="Facture déjà traitée" arrow placement="left">
                                  <CheckCircleOutlineIcon
                                    sx={{
                                      color: '#4caf50',
                                      fontSize: 20,
                                      cursor: 'help',
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Tooltip title={row.billing_reason ?? 'En attente de traitement'} arrow placement="left">
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'help' }}>
                                    <InfoOutlinedIcon
                                      sx={{
                                        color: PENDING_STATE_STYLE[row.billing_state ?? 'awaiting_receipt']?.color ?? '#ff9800',
                                        fontSize: 20,
                                      }}
                                    />
                                    <span style={{ fontSize: 12, color: '#555' }}>
                                      {PENDING_STATE_STYLE[row.billing_state ?? 'awaiting_receipt']?.label ?? 'En attente'}
                                    </span>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })}
          </Paper>
        ))
      )}

      <AgencyInfoDialog clientId={selectedClientId} open={dialogOpen} onClose={handleClose} />
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CGPendingInvoicesList;
